import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Building, Plus, Save, Trash2 } from 'lucide-react'
import { useDirty } from '@/hooks/useDirty'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { Checkbox } from '@/components/ui/checkbox'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { CountryCombobox } from '@/components/CountryCombobox'
import { CurrencyCombobox } from '@/components/CurrencyCombobox'
import { StateProvinceField } from '@/components/StateProvinceField'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import {
  useVendor,
  useUpdateVendor,
  useDeactivateVendor,
  useVendorBankAccounts,
  useUpsertVendorBankAccount,
} from '@/features/ap/queries'
import type { VendorBankAccount, VendorPayload } from '@/features/ap/types'
import { countryForCurrency, formatDateTime, normalizeCountryCode } from '@/lib/locale'

function blankBank(): Partial<VendorBankAccount> {
  return {
    label: 'Primary',
    account_holder_name: '',
    bank_name: '',
    account_number_last4: '',
    routing_number: '',
    iban: '',
    swift: '',
    country: '',
    is_primary: true,
  }
}

export function VendorDetailPage() {
  const { id } = useParams<{ id: string }>()
  const vendorId = parseInt(id as string, 10)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasPermission } = useAuth()
  const regional = useRegionalDefaults()
  const canEditVendor = hasPermission('invoicing.vendors.manage')
  const canViewBank = hasPermission('invoicing.vendors.bank.view')
  const canManageBank = hasPermission('invoicing.vendors.bank.manage')

  const vendorQ = useVendor(vendorId)
  const banksQ = useVendorBankAccounts(canViewBank ? vendorId : undefined)
  const updateMut = useUpdateVendor()
  const deactivateMut = useDeactivateVendor()
  const upsertBankMut = useUpsertVendorBankAccount(vendorId)

  const [form, setForm] = useState<VendorPayload | null>(null)
  const [bankOpen, setBankOpen] = useState(false)
  const [bank, setBank] = useState<Partial<VendorBankAccount>>(blankBank())
  const [deactivateOpen, setDeactivateOpen] = useState(false)

  useEffect(() => {
    const v = vendorQ.data?.vendor
    if (!v) return
    // Normalize the saved value first (legacy data may have stored
    // "United Kingdom" or "GBR"), then back-fill from default_currency
    // when nothing usable is on the record (e.g. GBP → GB).
    const country =
      normalizeCountryCode(v.country) ||
      countryForCurrency(v.default_currency) ||
      ''
    setForm({
      display_name: v.display_name,
      legal_name: v.legal_name || '',
      email: v.email || '',
      phone: v.phone || '',
      address_line1: v.address_line1 || '',
      address_line2: v.address_line2 || '',
      city: v.city || '',
      state_region: v.state_region || '',
      postal_code: v.postal_code || '',
      country,
      tax_id: v.tax_id || '',
      default_payment_terms_days: v.default_payment_terms_days,
      default_currency: v.default_currency,
      default_expense_account_code: v.default_expense_account_code || '',
      notes: v.notes || '',
    })
  }, [vendorQ.data])

  function setField<K extends keyof VendorPayload>(k: K, v: VendorPayload[K]) {
    setForm((prev) => prev ? { ...prev, [k]: v } : prev)
  }

  // Snapshot the saved vendor in the same VendorPayload shape so useDirty
  // diffs apples-to-apples against `form`. Numeric coercions match the
  // hydration block above (default_payment_terms_days / is_active are
  // primitives, the rest are strings).
  const vendorBaseline = useMemo<VendorPayload | null>(() => {
    const v = vendorQ.data?.vendor
    if (!v) return null
    const country =
      normalizeCountryCode(v.country) ||
      countryForCurrency(v.default_currency) ||
      ''
    return {
      display_name: v.display_name,
      legal_name: v.legal_name || '',
      email: v.email || '',
      phone: v.phone || '',
      address_line1: v.address_line1 || '',
      address_line2: v.address_line2 || '',
      city: v.city || '',
      state_region: v.state_region || '',
      postal_code: v.postal_code || '',
      country,
      tax_id: v.tax_id || '',
      default_payment_terms_days: v.default_payment_terms_days,
      default_currency: v.default_currency,
      default_expense_account_code: v.default_expense_account_code || '',
      notes: v.notes || '',
    }
  }, [vendorQ.data])
  const vendorDirty = useDirty(form, vendorBaseline)

  // Bank-account modal: baseline is either the row being edited or an
  // empty row when adding. The blankBank() helper above seeds is_primary
  // = true so a brand-new bank-account modal will be considered dirty
  // immediately - we mirror that here so we only flip Save once the user
  // has actually changed something or filled in at least the label.
  const bankBaseline = useMemo<Partial<VendorBankAccount> | null>(() => {
    if (!bankOpen) return null
    if (bank.id) {
      // Editing - find the original row to compare against.
      const original = (banksQ.data?.bank_accounts ?? []).find((b) => b.id === bank.id)
      if (!original) return null
      return {
        id: original.id,
        label: original.label,
        account_holder_name: original.account_holder_name || '',
        bank_name: original.bank_name || '',
        account_number_last4: original.account_number_last4 || '',
        routing_number: original.routing_number || '',
        iban: original.iban || '',
        swift: original.swift || '',
        country: original.country || '',
        is_primary: !!original.is_primary,
      }
    }
    // New - mirror the openNewBank() seed (label='Primary', is_primary=true,
    // country pre-filled from the vendor).
    return { ...blankBank(), country: form?.country || '' }
  }, [bankOpen, bank.id, banksQ.data, form?.country])
  const bankDirty = useDirty(bank, bankBaseline)

  async function onSave() {
    if (!form?.display_name?.trim()) {
      toast({ title: 'Display name is required', variant: 'destructive' }); return
    }
    try {
      await updateMut.mutateAsync({
        id: vendorId,
        body: { ...form, expected_updated_at: vendorQ.data?.vendor?.updated_at ?? null },
      })
      toast({ title: 'Vendor updated' })
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Save failed', variant: 'destructive' })
    }
  }

  async function onDeactivateConfirm() {
    try {
      await deactivateMut.mutateAsync(vendorId)
      toast({ title: 'Vendor deactivated' })
      navigate('/invoicing/vendors')
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed', variant: 'destructive' })
    }
  }

  function openNewBank() {
    // Seed the new bank's country from the vendor's country (which itself
    // is back-filled from default_currency when missing) so a GBP vendor's
    // first bank account opens with United Kingdom already picked.
    setBank({ ...blankBank(), country: form?.country || '' })
    setBankOpen(true)
  }

  function openEditBank(b: VendorBankAccount) {
    setBank({
      id: b.id,
      label: b.label,
      account_holder_name: b.account_holder_name || '',
      bank_name: b.bank_name || '',
      account_number_last4: b.account_number_last4 || '',
      routing_number: b.routing_number || '',
      iban: b.iban || '',
      swift: b.swift || '',
      country: b.country || '',
      is_primary: !!b.is_primary,
    })
    setBankOpen(true)
  }

  async function onBankSave() {
    try {
      await upsertBankMut.mutateAsync(bank)
      toast({ title: bank.id ? 'Bank account updated' : 'Bank account added' })
      setBankOpen(false)
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed', variant: 'destructive' })
    }
  }

  const banks = useMemo(() => banksQ.data?.bank_accounts ?? [], [banksQ.data])

  if (vendorQ.isLoading || !form) {
    return <Page title="Vendor"><Card><div className="flex justify-center py-20"><LoadingSpinner /></div></Card></Page>
  }
  if (!vendorQ.data?.vendor) {
    return <Page title="Vendor not found"><Card><EmptyState title="Not found" description="This vendor doesn't exist." /></Card></Page>
  }
  const vendor = vendorQ.data.vendor

  return (
    <Page
      title={vendor.display_name}
      description={vendor.is_active ? 'Active vendor' : 'Inactive vendor'}
      actions={
        <Toolbar align="end">
          <Button variant="ghost" onClick={() => navigate('/invoicing/vendors')}>
            <ArrowLeft className="mr-1 size-4" /> Back
          </Button>
          <Button variant="secondary" asChild>
            <Link to={`/invoicing/bills/new?vendor_id=${vendor.id}`}>New bill</Link>
          </Button>
          {canEditVendor && vendor.is_active ? (
            <Button variant="danger" onClick={() => setDeactivateOpen(true)}>
              <Trash2 className="mr-1 size-4" /> Deactivate
            </Button>
          ) : null}
          {canEditVendor ? (
            <Button onClick={onSave} disabled={!vendorDirty || updateMut.isPending}>
              <Save className="mr-1 size-4" /> Save
            </Button>
          ) : null}
        </Toolbar>
      }
    >
      <Card>
        <div className="grid gap-4 p-6 md:grid-cols-2">
          <FormField label="Display name" required value={form.display_name} onChange={e => setField('display_name', e.currentTarget.value)} disabled={!canEditVendor} />
          <FormField label="Legal name" value={form.legal_name || ''} onChange={e => setField('legal_name', e.currentTarget.value)} disabled={!canEditVendor} />
          <FormField label="Email" type="email" value={form.email || ''} onChange={e => setField('email', e.currentTarget.value)} disabled={!canEditVendor} />
          <FormField label="Phone" value={form.phone || ''} onChange={e => setField('phone', e.currentTarget.value)} disabled={!canEditVendor} />
          <FormField label="Tax ID / VAT" value={form.tax_id || ''} onChange={e => setField('tax_id', e.currentTarget.value)} disabled={!canEditVendor} />
          <FormField
            label="Default expense account"
            value={form.default_expense_account_code || ''}
            onChange={e => setField('default_expense_account_code', e.currentTarget.value)}
            disabled={!canEditVendor}
            hint="Pre-fills line-item account on new bills."
          />
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="mb-3 text-sm font-semibold">Address</div>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Address line 1" value={form.address_line1 || ''} onChange={e => setField('address_line1', e.currentTarget.value)} disabled={!canEditVendor} />
            <FormField label="Address line 2" value={form.address_line2 || ''} onChange={e => setField('address_line2', e.currentTarget.value)} disabled={!canEditVendor} />
            <FormField label="City" value={form.city || ''} onChange={e => setField('city', e.currentTarget.value)} disabled={!canEditVendor} />
            <FormField
              label={
                form.country?.toUpperCase() === 'US'
                  ? 'State'
                  : form.country?.toUpperCase() === 'CA'
                    ? 'Province'
                    : 'State / region'
              }
              control={
                <StateProvinceField
                  country={form.country}
                  value={form.state_region || ''}
                  onChange={v => setField('state_region', v)}
                  disabled={!canEditVendor}
                />
              }
            />
            <FormField label="Postal code" value={form.postal_code || ''} onChange={e => setField('postal_code', e.currentTarget.value)} disabled={!canEditVendor} />
            <FormField
              label="Country"
              control={
                <CountryCombobox
                  value={form.country || null}
                  onChange={v => {
                    setField('country', v || '')
                    setField('state_region', '')
                  }}
                  onCurrencyChange={c => {
                    // Only fill currency when it hasn't been set -
                    // preserve UK-address-but-USD-billing style picks.
                    if (!form.default_currency) {
                      setField('default_currency', c)
                    }
                  }}
                />
              }
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="grid gap-4 p-6 md:grid-cols-3">
          <FormField label="Default payment terms (days)" type="number" value={String(form.default_payment_terms_days ?? 30)} onChange={e => setField('default_payment_terms_days', Number(e.currentTarget.value) || 0)} disabled={!canEditVendor} />
          <FormField
            label="Default currency"
            control={
              <CurrencyCombobox
                value={form.default_currency || regional.currency}
                onChange={v => {
                  setField('default_currency', v)
                  if (!form.country) {
                    const derived = countryForCurrency(v)
                    if (derived) setField('country', derived)
                  }
                }}
              />
            }
          />
          <FormField
            label="Notes"
            control={<textarea className="min-h-[72px] w-full rounded-md border bg-card px-3 py-2 text-sm" value={form.notes || ''} onChange={e => setField('notes', e.currentTarget.value)} disabled={!canEditVendor} />}
          />
        </div>
      </Card>

      {canViewBank ? (
        <Card>
          <div className="p-6">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Bank accounts</div>
                <p className="text-xs text-muted-foreground">
                  Editing or adding a bank account stamps the change time. Approval policies may require a compliance review on the next payment to vendors with recently changed banks.
                </p>
              </div>
              {canManageBank ? (
                <Button variant="secondary" onClick={openNewBank}>
                  <Plus className="mr-1 size-4" /> Add account
                </Button>
              ) : null}
            </div>
            {banksQ.isLoading ? (
              <LoadingSpinner />
            ) : banks.length === 0 ? (
              <EmptyState
                icon={Building}
                title="No bank accounts on file"
                description="Add a bank account so payments to this vendor can route through ACH or wire."
                action={canManageBank ? <Button variant="secondary" onClick={openNewBank}><Plus className="mr-1 size-4" /> Add account</Button> : null}
              />
            ) : (
              <ul className="space-y-2">
                {banks.map(b => (
                  <li key={b.id} className="rounded-md border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">
                          {b.label}
                          {b.is_primary ? <Badge variant="success" className="ml-2">primary</Badge> : null}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {[b.bank_name, b.account_holder_name].filter(Boolean).join(' · ')}
                          {b.account_number_last4 ? <> · ****{b.account_number_last4}</> : null}
                        </div>
                        {b.bank_account_last_changed_at ? (
                          <div className="text-xs text-muted-foreground">
                            Last changed: {formatDateTime(b.bank_account_last_changed_at)}
                          </div>
                        ) : null}
                      </div>
                      {canManageBank ? (
                        <Button size="sm" variant="ghost" onClick={() => openEditBank(b)}>Edit</Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      ) : null}

      <Modal open={bankOpen} onOpenChange={setBankOpen} title={bank.id ? 'Edit bank account' : 'New bank account'}>
        <div className="space-y-3">
          <FormField label="Label" value={bank.label || ''} onChange={e => setBank({ ...bank, label: e.currentTarget.value })} hint="e.g. Primary, Operations, USD" />
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Account holder name" value={bank.account_holder_name || ''} onChange={e => setBank({ ...bank, account_holder_name: e.currentTarget.value })} />
            <FormField label="Bank name" value={bank.bank_name || ''} onChange={e => setBank({ ...bank, bank_name: e.currentTarget.value })} />
            <FormField label="Account # (last 4 only)" value={bank.account_number_last4 || ''} onChange={e => setBank({ ...bank, account_number_last4: e.currentTarget.value })} />
            <FormField label="Routing #" value={bank.routing_number || ''} onChange={e => setBank({ ...bank, routing_number: e.currentTarget.value })} />
            <FormField label="IBAN" value={bank.iban || ''} onChange={e => setBank({ ...bank, iban: e.currentTarget.value })} />
            <FormField label="SWIFT/BIC" value={bank.swift || ''} onChange={e => setBank({ ...bank, swift: e.currentTarget.value })} />
            <FormField
              label="Country"
              control={<CountryCombobox value={bank.country || null} onChange={v => setBank({ ...bank, country: v || '' })} />}
            />
            <FormField
              label="Primary"
              control={
                <div className="flex h-10 items-center gap-2 rounded-md border bg-card px-3">
                  <Checkbox
                    checked={!!bank.is_primary}
                    onCheckedChange={(v) => setBank({ ...bank, is_primary: v === true })}
                  />
                  <span className="text-sm text-muted-foreground">Use this account for payments by default</span>
                </div>
              }
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBankOpen(false)}>Cancel</Button>
            <Button onClick={onBankSave} disabled={!bankDirty || upsertBankMut.isPending}>Save</Button>
          </div>
        </div>
      </Modal>

      <Modal.Confirm
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        title="Deactivate vendor?"
        body="The vendor will be hidden from new-bill pickers. Existing bills + payment history remain intact."
        confirmLabel="Deactivate"
        confirmVariant="danger"
        onConfirm={onDeactivateConfirm}
        busy={deactivateMut.isPending}
      />
    </Page>
  )
}
